import { z } from 'zod';
import {
  exceptionReportSchema,
  exceptionSeverityValues,
  exceptionStatusValues,
} from '@/features/mass-balance/types';

export { exceptionReportSchema, exceptionSeverityValues, exceptionStatusValues };
export type {
  ExceptionReportDto,
  ExceptionSeverity,
  ExceptionStatus,
} from '@/features/mass-balance/types';

type TFn = (key: string, opts?: Record<string, unknown>) => string;

/**
 * FR-008: the 17-type reportable taxonomy (mirrors the backend allowlist). The
 * first six are field-observed differences; the rest mirror the directly-
 * observable mass-balance engine codes (spec-05 §6). Labels fall back to the
 * raw code when no i18n string exists.
 */
export const reportableExceptionCodes = [
  'quality_difference',
  'packaging_difference',
  'wrong_shipment',
  'quantity_abnormality',
  'weight_difference',
  'other',
  'UNAPPROVED_INCOMING_ITEM',
  'UNAPPROVED_MATERIAL',
  'PENDING_NORMALIZATION',
  'MISSING_SORTING_DECISION',
  'UNCONFIRMED_DISMANTLING_SESSION',
  'UNAPPROVED_SECONDARY_DESTINATION',
  'MISSING_SECONDARY_RECEIPT',
  'UNAPPROVED_SHIPMENT_DESTINATION',
  'MISSING_SHIPMENT_RECEIPT',
  'NEGATIVE_INVENTORY',
  'SHIPMENT_EXCEEDS_AVAILABLE_INVENTORY',
] as const;

/** FR-007: codes whose resolve/waive REQUIRES an explicit decision. */
export const decisionRequiredCodes = ['quality_difference', 'packaging_difference'] as const;

export const decisionValues = ['return', 'onsite_processing', 'no_action', 'other'] as const;

export const isDecisionRequired = (code: string): boolean =>
  (decisionRequiredCodes as readonly string[]).includes(code);

export const buildResolveSchema = (t: TFn, requireDecision: boolean) =>
  z
    .object({
      resolution_note: z.string().min(1, t('efs.exceptions.validation.note_required')).max(2000),
      decision: z.enum(decisionValues).optional(),
      decision_note: z.string().max(2000).optional(),
    })
    .refine((v) => !requireDecision || !!v.decision, {
      path: ['decision'],
      message: t('efs.exceptions.validation.decision_required'),
    });
export type ResolveInput = {
  resolution_note: string;
  decision?: (typeof decisionValues)[number];
  decision_note?: string;
};

export const buildCreateSchema = (t: TFn) =>
  z.object({
    exception_code: z.enum(reportableExceptionCodes),
    severity: z.enum(exceptionSeverityValues),
    description: z.string().min(1, t('efs.exceptions.validation.description_required')).max(4000),
    cause: z.string().max(4000).optional(),
    affects_quotation: z.boolean(),
    additional_cost: z.boolean(),
    // FR-008 binding: every report names ≥1 business object. The dialog binds
    // to the batch by default; a finer object picker is a later enhancement.
    target_record_type: z.string().min(1),
    target_record_id: z.string().min(1),
  });
export type CreateExceptionInput = z.infer<ReturnType<typeof buildCreateSchema>>;

export interface ExceptionListQuery {
  severity?: string;
  status?: string;
  monthly_batch_id?: string;
  exception_code?: string;
  page?: number;
  per_page?: number;
}

export interface ExceptionListMeta {
  total: number;
  page: number;
  per_page: number;
}
